/**
 * Training API Service
 * Handles secure communication with backend training API
 *
 * This service provides a unified interface for all training-related API calls.
 * Methods are organized into logical groups:
 * - Dataset generation
 * - Model training
 * - Status and results retrieval
 * - Model management (save, list, download)
 */

import { ModelParameters } from '../../components/ModelConfiguration';
import { TrainingDataSplitData } from '../../components/TrainingDataSplit';
import { TrainingApiResponse, ModelListResponse } from './types';
import { createStructuredError } from './utils';
import {
  generateDatasets,
  trainModels,
  startCompletePipeline,
  getTrainingStatus,
  getPipelineOverview,
  getTrainingResults,
  getComprehensiveEvaluation,
  getVisualizations,
  saveModel,
  listModels,
  downloadModel,
  listModelsDatabase,
  downloadModelH5,
  predictWithModel,
  PredictionResult,
  saveForecastConfig,
  getForecastConfig,
  getApiParameters,
  SaveForecastConfigPayload,
  ApiParameter,
  generateApiKey,
  listApiKeys,
  revokeApiKey,
  ApiKeyInfo,
  GenerateApiKeyResponse,
} from './methods';

export class TrainingApiService {
  /**
   * Generate datasets with proper parameter validation
   */
  static generateDatasets(
    sessionId: string,
    modelConfiguration: ModelParameters,
    trainingDataSplit: TrainingDataSplitData
  ): Promise<TrainingApiResponse> {
    return generateDatasets(sessionId, modelConfiguration, trainingDataSplit);
  }

  /**
   * Train models with proper parameter validation
   */
  static trainModels(
    sessionId: string,
    modelConfiguration: ModelParameters,
    trainingDataSplit?: TrainingDataSplitData
  ): Promise<TrainingApiResponse> {
    return trainModels(sessionId, modelConfiguration, trainingDataSplit);
  }

  /**
   * Start complete pipeline with validation
   */
  static startCompletePipeline(
    sessionId: string,
    modelConfiguration: ModelParameters,
    trainingDataSplit: TrainingDataSplitData
  ): Promise<TrainingApiResponse> {
    return startCompletePipeline(sessionId, modelConfiguration, trainingDataSplit);
  }

  /**
   * Get training status
   */
  static getTrainingStatus(sessionId: string): Promise<TrainingApiResponse> {
    return getTrainingStatus(sessionId);
  }

  /**
   * Get pipeline overview
   */
  static getPipelineOverview(sessionId: string): Promise<TrainingApiResponse> {
    return getPipelineOverview(sessionId);
  }

  /**
   * Get training results
   */
  static getTrainingResults(sessionId: string): Promise<TrainingApiResponse> {
    return getTrainingResults(sessionId);
  }

  /**
   * Get comprehensive evaluation
   */
  static getComprehensiveEvaluation(sessionId: string): Promise<TrainingApiResponse> {
    return getComprehensiveEvaluation(sessionId);
  }

  /**
   * Get visualizations
   */
  static getVisualizations(sessionId: string): Promise<TrainingApiResponse> {
    return getVisualizations(sessionId);
  }

  /**
   * Create structured error from API error
   */
  static createStructuredError(error: unknown, context: string) {
    return createStructuredError(error, context);
  }

  /**
   * Save trained model
   */
  static saveModel(sessionId: string, modelName?: string): Promise<TrainingApiResponse> {
    return saveModel(sessionId, modelName);
  }

  /**
   * List all available models for a session
   */
  static listModels(sessionId: string): Promise<TrainingApiResponse<ModelListResponse>> {
    return listModels(sessionId);
  }

  /**
   * Download a model file
   */
  static downloadModel(sessionId: string, modelName?: string): Promise<void> {
    return downloadModel(sessionId, modelName);
  }

  /**
   * List models from Supabase database
   */
  static listModelsDatabase(sessionId: string): Promise<TrainingApiResponse<ModelListResponse>> {
    return listModelsDatabase(sessionId);
  }

  /**
   * Download model in .h5 format from Supabase database
   */
  static downloadModelH5(sessionId: string, modelFilename?: string): Promise<void> {
    return downloadModelH5(sessionId, modelFilename);
  }

  /**
   * Make predictions using a trained model
   * Note: Requires backend endpoint POST /api/training/predict/{session_id}
   */
  static predictWithModel(
    sessionId: string,
    modelFilename: string,
    inputData: Record<string, number>[]
  ): Promise<TrainingApiResponse<PredictionResult>> {
    return predictWithModel(sessionId, modelFilename, inputData);
  }

  static saveForecastConfig(
    sessionId: string,
    payload: SaveForecastConfigPayload
  ): Promise<TrainingApiResponse> {
    return saveForecastConfig(sessionId, payload);
  }

  /**
   * Get forecast configuration for a session
   */
  static getForecastConfig(sessionId: string): Promise<TrainingApiResponse> {
    return getForecastConfig(sessionId);
  }

  /**
   * Get available API parameters
   */
  static getApiParameters(apiSource?: string): Promise<TrainingApiResponse<{ parameters: ApiParameter[] }>> {
    return getApiParameters(apiSource);
  }

  static generateApiKey(sessionId: string, name: string, expiresInDays?: number | null): Promise<TrainingApiResponse<GenerateApiKeyResponse>> {
    return generateApiKey(sessionId, name, expiresInDays);
  }

  static listApiKeys(sessionId: string): Promise<TrainingApiResponse<{ keys: ApiKeyInfo[] }>> {
    return listApiKeys(sessionId);
  }

  static revokeApiKey(keyId: string): Promise<TrainingApiResponse> {
    return revokeApiKey(keyId);
  }
}
